import { useState, useEffect, useContext, useMemo } from "react";
import api from '../api/axios'; 
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { PageContainer, PageTitleBadge, DataGridTable, SearchBar, FilterSelect, FilterDateGroup, FiltersContainer } from "../components";
import { FaHistory } from "react-icons/fa";
import "../styles/common.css";
import "./Historique.css";

export default function Historique() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animaux, setAnimaux] = useState([]);
  const [filtres, setFiltres] = useState({
    action: '',
    date_debut: '',
    date_fin: '',
    animal: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // État pour le zoom
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('historique-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('historique-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchHistorique = async () => {
      setLoading(true);
      setError(null);
      try {
        const animauxResponse = await api.get('/api/animaux/');
        setAnimaux(animauxResponse.data);
        const params = new URLSearchParams();
        if (filtres.action) params.append('action', filtres.action);
        if (filtres.date_debut) params.append('date_action_after', filtres.date_debut);
        if (filtres.date_fin) params.append('date_action_before', filtres.date_fin);
        if (filtres.animal) params.append('animal', filtres.animal);
        if (searchTerm) params.append('search', searchTerm);

        const response = await api.get('/api/historique/', { params });
        setHistorique(response.data);
      } catch (err) {
        setError(t.errorLoadingHistory);
      } finally {
        setLoading(false);
      }
    };
    fetchHistorique();
  }, [filtres, searchTerm]);

  const handleFiltreChange = (e) => {
    setFiltres({
      ...filtres,
      [e.target.name]: e.target.value,
    });
  };

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    const mappedHistory = historique.map(item => {
      const dateStr = item.date_action || '';
      const timestamp = dateStr ? new Date(dateStr).getTime() : 0;
      
      return {
        id: item.id,
        animal: item.animal_matricule || item.animal || "N/A",
        action: item.action,
        utilisateur: item.utilisateur_nom || item.utilisateur || "N/A",
        berger: item.berger_nom || (item.berger ? t.notAssigned : "-"),
        date_action: item.date_action,
        description: item.description || t.noDetails,
        timestamp: timestamp,
      };
    });
    
    // Trier par date décroissante puis par ID décroissant
    return mappedHistory.sort((a, b) => {
      if (b.timestamp !== a.timestamp) {
        return b.timestamp - a.timestamp;
      }
      return b.id - a.id;
    });
  }, [historique]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'animal',
      headerName: t.animal,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'action',
      headerName: t.action,
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'utilisateur',
      headerName: t.user,
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'berger',
      headerName: t.shepherd,
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'date_action',
      headerName: t.date,
      width: 130,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'description',
      headerName: t.description,
      width: 250,
      flex: 1,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
  ], [t]);

  const columns = useMemo(() => {
    return baseColumns;
  }, [baseColumns]);

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">{t.loadingHistory}</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaHistory />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.historyTitle} icon={<FaHistory />} />

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchHistory}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* Filtres */}
      <FiltersContainer title={t.filters}>
        <FilterSelect
          name="action"
          value={filtres.action}
          onChange={handleFiltreChange}
          options={[
            { value: "soin", label: t.care },
            { value: "vaccination", label: t.vaccination },
            { value: "traitement", label: t.treatment },
            { value: "alimentation", label: t.feeding },
            { value: "autre", label: t.other }
          ]}
          placeholder={t.filterByAction}
          className="filtre-action"
          width="200px"
        />
        <FilterSelect
          name="animal"
          value={filtres.animal}
          onChange={handleFiltreChange}
          options={animaux.map(animal => ({ id: animal.id, nom: animal.matricule }))}
          placeholder={t.filterByAnimal}
          className="filtre-animal"
          width="200px"
        />
        <FilterDateGroup
          label={t.startDate}
          name="date_debut"
          value={filtres.date_debut}
          onChange={handleFiltreChange}
        />
        <FilterDateGroup
          label={t.endDate}
          name="date_fin"
          value={filtres.date_fin}
          onChange={handleFiltreChange}
        />
      </FiltersContainer>

      {/* DataGrid */}
      {historique.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">{t.noHistoryRecorded}</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="historique-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'date_action', sort: 'desc' }}
          pageSize={25}
          containerClassName="historique-grid-container"
          initialState={{
            sorting: { 
              sortModel: [
                { field: 'date_action', sort: 'desc' },
                { field: 'id', sort: 'desc' }
              ] 
            }
          }}
        />
      )}
    </PageContainer>
  );
}

